import { useRef, type MouseEvent, type ReactNode } from 'react';
import { motion, useMotionTemplate, useSpring } from 'motion/react';
import { cn } from '../../lib/cn';

const TILT_SPRING = { stiffness: 300, damping: 24, mass: 0.6 };
const MAX_TILT_DEG = 8;

/** Wraps its children in a card that tilts toward the cursor in 3D and shows
 * a soft light glare tracking the pointer. Pure desktop-hover embellishment:
 * on touch, mouse events never fire so the springs stay at rest and the card
 * renders exactly as it did before this existed. */
export default function TiltCard({ children, className }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const reducedMotion = useRef(typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches).current;

  const rotateX = useSpring(0, TILT_SPRING);
  const rotateY = useSpring(0, TILT_SPRING);
  const glareX = useSpring(50, TILT_SPRING);
  const glareY = useSpring(50, TILT_SPRING);
  const glareOpacity = useSpring(0, TILT_SPRING);

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    if (reducedMotion) return;
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    rotateY.set((px - 0.5) * MAX_TILT_DEG * 2);
    rotateX.set((0.5 - py) * MAX_TILT_DEG * 2);
    glareX.set(px * 100);
    glareY.set(py * 100);
    glareOpacity.set(1);
  };

  const reset = () => {
    rotateX.set(0);
    rotateY.set(0);
    glareOpacity.set(0);
  };

  const glareBackground = useMotionTemplate`radial-gradient(circle at ${glareX}% ${glareY}%, rgba(255,255,255,0.4), transparent 55%)`;

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={reset}
      style={{ rotateX, rotateY, transformPerspective: 700 }}
      className={cn('relative', className)}
    >
      {children}
      <motion.div aria-hidden className="pointer-events-none absolute inset-0 rounded-[inherit]" style={{ background: glareBackground, opacity: glareOpacity }} />
    </motion.div>
  );
}
